import { useEffect, useState } from "react";
import { Loader2, Clock, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/AdminGate";
import PageHeader from "@/components/PageHeader";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminHoursPage() {
  useSeo({ title: "Admin · Opening Hours | Cool Runnings Cape Town", noindex: true });
  return (
    <AdminGate>
      <HoursEditor />
    </AdminGate>
  );
}

function HoursEditor() {
  const [form, setForm] = useState(null);
  const [todayForm, setTodayForm] = useState({ open: "", close: "" });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [todaySaved, setTodaySaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/hours`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setForm(d);
        const weekdayName = new Intl.DateTimeFormat("en-ZA", { timeZone: "Africa/Johannesburg", weekday: "long" }).format(new Date());
        const normal = ["Saturday", "Sunday"].includes(weekdayName)
          ? { open: d.weekend_open, close: d.weekend_close }
          : { open: d.weekday_open, close: d.weekday_close };
        setTodayForm(d.today ? { open: d.today.open, close: d.today.close } : normal);
      })
      .catch(() => toast.error("Could not load the opening hours"));
  }, []);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setSaved(false);
  };

  const setToday = (key) => (e) => {
    setTodayForm({ ...todayForm, [key]: e.target.value });
    setTodaySaved(false);
  };

  const saveToday = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/hours/today`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ open: todayForm.open, close: todayForm.close }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail);
      setTodayForm({ open: d.today.open, close: d.today.close });
      setTodaySaved(true);
      toast.success("Today's hours saved — the traffic light shows them now");
    } catch (err) {
      toast.error(err.message || "Could not save — please try again");
    } finally {
      setBusy(false);
    }
  };

  const resetToday = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/hours/today`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ clear: true }),
      });
      if (!res.ok) throw new Error();
      const weekdayName = new Intl.DateTimeFormat("en-ZA", { timeZone: "Africa/Johannesburg", weekday: "long" }).format(new Date());
      const normal = ["Saturday", "Sunday"].includes(weekdayName)
        ? { open: form.weekend_open, close: form.weekend_close }
        : { open: form.weekday_open, close: form.weekday_close };
      setTodayForm(normal);
      setTodaySaved(false);
      setForm({ ...form, today: null });
      toast.success("Back to the normal times for today");
    } catch {
      toast.error("Could not save — please try again");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/hours`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setForm(d);
      setSaved(true);
      toast.success("Opening hours saved — the website now shows the new times");
    } catch {
      toast.error("Could not save — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        pageName="Opening Hours"
        eyebrow="Staff area"
        title="OPENING HOURS"
        goodgirlTitle
        image="/admin-header.jpg"
      />
      <section className="mx-auto max-w-3xl px-4 pb-24 pt-16 sm:px-6" data-testid="admin-hours-section">
        {!form ? (
          <p className="flex items-center justify-center gap-2 py-20 text-lg font-bold text-[#6b5a3a]" data-testid="admin-hours-loading">
            <Loader2 size={20} className="animate-spin" /> Loading…
          </p>
        ) : (
          <>
            <p className="text-xl font-extrabold text-[#241c0d]" data-testid="admin-hours-intro">
              Type the times below and press the big green button. That's all.
            </p>

            <div className="mt-8 space-y-6">
              <div className="card-dark p-8 ring-4 ring-[#f8b818]" data-testid="admin-hours-today-card">
                <h3 className="text-2xl font-extrabold text-[#241c0d]" data-testid="admin-hours-today-heading">
                  TODAY only — {new Intl.DateTimeFormat("en-ZA", { timeZone: "Africa/Johannesburg", weekday: "long", day: "numeric", month: "long" }).format(new Date())}
                </h3>
                <p className="mt-1 text-base font-bold text-[#6b5a3a]">
                  Different times for today only — a public holiday, an early close, a rainy day. Tomorrow goes
                  back to the normal times by itself.
                </p>
                {form.today?.date ? (
                  <p className="mt-3 rounded-xl bg-[#16a34a]/15 px-4 py-3 text-base font-extrabold text-[#15803d]" data-testid="admin-hours-today-active">
                    Today's special hours are ON: {form.today.open} – {form.today.close}
                  </p>
                ) : (
                  <p className="mt-3 rounded-xl bg-[#f8b818]/20 px-4 py-3 text-base font-bold text-[#6b5a3a]" data-testid="admin-hours-today-inactive">
                    Today runs on the normal times.
                  </p>
                )}
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <TimeBox label="Opens today at" value={todayForm.open} onChange={setToday("open")} testid="admin-hours-today-open" />
                  <TimeBox label="Closes today at" value={todayForm.close} onChange={setToday("close")} testid="admin-hours-today-close" />
                </div>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={saveToday}
                    disabled={busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#16a34a] px-6 py-4 text-lg font-black text-white shadow-[0_10px_25px_rgba(22,163,74,0.35)] transition-all hover:bg-[#15803d] active:scale-[0.99] disabled:opacity-60"
                    data-testid="admin-hours-today-save-btn"
                  >
                    {busy ? <Loader2 size={20} className="animate-spin" /> : todaySaved ? <CheckCircle2 size={20} /> : <Clock size={20} />}
                    {busy ? "Saving…" : "Save today's hours"}
                  </button>
                  <button
                    onClick={resetToday}
                    disabled={busy}
                    className="flex items-center justify-center gap-2 rounded-2xl border-2 border-[#241c0d]/25 bg-white px-6 py-4 text-lg font-black text-[#241c0d] transition-all hover:bg-[#fff7e8] active:scale-[0.99] disabled:opacity-60"
                    data-testid="admin-hours-today-reset-btn"
                  >
                    Back to normal times
                  </button>
                </div>
              </div>

              <div className="card-dark p-8" data-testid="admin-hours-weekday-card">
                <h3 className="text-2xl font-extrabold text-[#241c0d]">Monday to Friday</h3>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <TimeBox label="Opens at" value={form.weekday_open} onChange={set("weekday_open")} testid="admin-hours-weekday-open" />
                  <TimeBox label="Closes at" value={form.weekday_close} onChange={set("weekday_close")} testid="admin-hours-weekday-close" />
                </div>
              </div>

              <div className="card-dark p-8" data-testid="admin-hours-weekend-card">
                <h3 className="text-2xl font-extrabold text-[#241c0d]">Weekends, Public- &amp; School Holidays</h3>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <TimeBox label="Opens at" value={form.weekend_open} onChange={set("weekend_open")} testid="admin-hours-weekend-open" />
                  <TimeBox label="Closes at" value={form.weekend_close} onChange={set("weekend_close")} testid="admin-hours-weekend-close" />
                </div>
              </div>
            </div>

            <button
              onClick={save}
              disabled={busy}
              className="mt-10 flex w-full items-center justify-center gap-3 rounded-2xl bg-[#16a34a] px-8 py-6 text-2xl font-black text-white shadow-[0_12px_30px_rgba(22,163,74,0.4)] transition-all hover:bg-[#15803d] active:scale-[0.99] disabled:opacity-60"
              data-testid="admin-hours-save-btn"
            >
              {busy ? <Loader2 size={26} className="animate-spin" /> : saved ? <CheckCircle2 size={26} /> : <Save size={26} />}
              {busy ? "Saving…" : saved ? "Saved" : "Save Opening Hours"}
            </button>

            <p className="mt-6 flex flex-wrap items-center justify-center gap-2 text-center text-base font-bold text-[#6b5a3a]" data-testid="admin-hours-preview">
              <Clock size={16} /> The website shows: Mon–Fri {form.weekday_open || "…"} – {form.weekday_close || "…"} · Weekends &amp; Holidays {form.weekend_open || "…"} – {form.weekend_close || "…"}
              {form.today?.date ? ` · TODAY ONLY ${form.today.open} – ${form.today.close}` : ""}
            </p>
            <p className="mt-2 text-center text-xs font-semibold text-[#8a7b5e]">
              Write times like 09h00 or 09:00. The park stays closed when it rains.
            </p>
          </>
        )}
      </section>
    </>
  );
}

function TimeBox({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-lg font-extrabold text-[#5f5233]">{label}</span>
      <input
        value={value || ""}
        onChange={onChange}
        placeholder="09h00"
        className="mt-2 w-full rounded-2xl border-2 border-[#241c0d]/20 bg-white px-5 py-4 text-3xl font-black text-[#241c0d] focus:border-[#f8b818] focus:outline-none"
        data-testid={testid}
      />
      {value && !/^(\d{1,2})[h:](\d{2})$/.test(value.trim()) && (
        <span className="mt-1 block text-xs font-bold text-[#d63a2f]">Use a time like 09h00</span>
      )}
    </label>
  );
}